import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { FaCog, FaEdit, FaTrash } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import { formatCurrency, formatCurrencyLabel } from '../../utils/formatUtils';
import NewSubscriptionModal from './NewSubscriptionModal';
import AddLicenseModal from './AddLicenseModal';
import CreateInvoiceModal from './CreateInvoiceModal';
import EditInvoiceModal from './EditInvoiceModal';
import SubscriptionConfigModal from './SubscriptionConfigModal';
import LicenseTypeEntitlementsModal from './LicenseTypeEntitlementsModal';
import AdminFieldMigrationModal from './AdminFieldMigrationModal';
import MultiSelectDropdown from '../../components/layout/MultiSelectDropdown1';
import { allowedObjectsForDisplay } from './AdminPlanConfigConstants';
import '../styles/admin-theme.css';

const FIELD_LABELS = {
  id: 'ID',
  name: 'Company Name',
  tenantcode: 'Tenant Code',
  userlicenses: 'User Licenses',
  isactive: 'Active',
  systememail: 'System Email',
  adminemail: 'Admin Email',
  logourl: 'Logo URL',
  sidebarbgurl: 'Sidebar Background URL',
  ispremium: 'Premium',
  sourceschema: 'Source Schema',
  city: 'City',
  street: 'Street',
  pincode: 'Pincode',
  state: 'State',
  country: 'Country',
  currency: 'Currency',
  enable_watconnect: 'WatConnect Enabled',
};

/** Format date for display. Uses date part only to avoid timezone shift (e.g. 2026-02-01 stays Feb 1). */
const formatDate = (d) => {
  if (d == null || d === '') return '-';
  try {
    if (typeof d === 'string') {
      const s = d.trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      if (d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      const date = new Date(d);
      if (Number.isNaN(date.getTime())) return String(d);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    if (typeof d.getTime === 'function') {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return String(d);
  } catch {
    return String(d);
  }
};

const AdminCompanyDetail = () => {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [showNewSubscriptionModal, setShowNewSubscriptionModal] = useState(false);
  const [showAddLicenseModal, setShowAddLicenseModal] = useState(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const [invoiceError, setInvoiceError] = useState('');
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editingLicense, setEditingLicense] = useState(null);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [deletingSubscription, setDeletingSubscription] = useState(false);
  const [deleteSubscriptionError, setDeleteSubscriptionError] = useState('');
  const [subscriptionConfigTarget, setSubscriptionConfigTarget] = useState(null);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [licenseTypesLoading, setLicenseTypesLoading] = useState(false);
  const [licenseTypeEntitlementsTarget, setLicenseTypeEntitlementsTarget] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [savingDefaultSchemaUser, setSavingDefaultSchemaUser] = useState(false);
  const [migrationSchemas, setMigrationSchemas] = useState([]);
  const [migrationSourceSchema, setMigrationSourceSchema] = useState('');
  const [migrationPlan, setMigrationPlan] = useState(null);
  const [migrationPlanLoading, setMigrationPlanLoading] = useState(false);
  const [migrationError, setMigrationError] = useState('');
  const [migrationSuccess, setMigrationSuccess] = useState(null);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [showFieldMigrationModal, setShowFieldMigrationModal] = useState(false);
  const [reconcileAdminTabsLoading, setReconcileAdminTabsLoading] = useState(false);
  const [reconcileAdminTabsMessage, setReconcileAdminTabsMessage] = useState('');
  const [reconcileAdminTabsAlertVariant, setReconcileAdminTabsAlertVariant] = useState('info');
  const [migrationStandardApps, setMigrationStandardApps] = useState([]);
  const [migrationSelectedAppIds, setMigrationSelectedAppIds] = useState([]);
  const [migrationTargetUsers, setMigrationTargetUsers] = useState([]);
  const [migrationSelectedTargetUserId, setMigrationSelectedTargetUserId] = useState('');
  const [migrationTargetUsersLoading, setMigrationTargetUsersLoading] = useState(false);
  const migrationSelectedTargetUserIdRef = useRef('');

  useEffect(() => {
    migrationSelectedTargetUserIdRef.current = migrationSelectedTargetUserId;
  }, [migrationSelectedTargetUserId]);

  useEffect(() => {
    if (!migrationError || !showMigrationModal) return;
    const rf = requestAnimationFrame(() => {
      document.getElementById('migration-modal-error-banner')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(rf);
  }, [migrationError, showMigrationModal]);

  const loadCompanyUsers = useCallback(async () => {
    if (!id) return;
    setCompanyUsersLoading(true);
    try {
      const list = await adminSchemaApi.fetchCompanyUsers(id);
      setCompanyUsers(Array.isArray(list) ? list : []);
    } catch {
      setCompanyUsers([]);
    } finally {
      setCompanyUsersLoading(false);
    }
  }, [id]);

  const handleDefaultSchemaUserChange = useCallback(async (e) => {
    const userId = e.target.value === '' ? null : e.target.value;
    if (!id) return;
    setSavingDefaultSchemaUser(true);
    try {
      await adminSchemaApi.setDefaultSchemaUser(id, userId);
      setCompany((prev) => (prev ? { ...prev, default_schema_user_id: userId } : null));
    } catch (err) {
      console.error('Failed to set default schema user:', err);
    } finally {
      setSavingDefaultSchemaUser(false);
    }
  }, [id]);

  const loadSubscriptions = useCallback(async () => {
    if (!id) return;
    setSubscriptionsLoading(true);
    try {
      const list = await adminSchemaApi.fetchSubscriptionsByCompanyId(id);
      setSubscriptions(Array.isArray(list) ? list : []);
    } catch {
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [id]);

  const loadLicenseTypes = useCallback(async () => {
    if (!id) return;
    setLicenseTypesLoading(true);
    try {
      const list = await adminSchemaApi.fetchCompanyLicenseTypes(id);
      setLicenseTypes(Array.isArray(list) ? list : []);
    } catch {
      setLicenseTypes([]);
    } finally {
      setLicenseTypesLoading(false);
    }
  }, [id]);

  const loadMigrationSchemas = useCallback(async () => {
    try {
      const list = await adminSchemaApi.fetchMigrationSchemas();
      setMigrationSchemas(Array.isArray(list) ? list : []);
    } catch {
      setMigrationSchemas([]);
    }
  }, []);

  const handleStartMigration = useCallback(async () => {
    if (!id || !migrationSourceSchema) return;
    if (migrationPlan?.targetSchemaExists && !migrationSelectedTargetUserId) return;
    setMigrationRunning(true);
    setMigrationError('');
    setMigrationSuccess(null);
    try {
      const selectedTargetUserId = migrationPlan?.targetSchemaExists ? migrationSelectedTargetUserId : null;
      const data = await adminSchemaApi.runMigration(id, migrationSourceSchema, true, migrationSelectedAppIds, selectedTargetUserId);
      const plan = await adminSchemaApi.fetchMigrationPlan(id, migrationSourceSchema);
      setMigrationPlan(plan);
      if (plan?.message) setMigrationError(plan.message);
      if (data?.message) {
        setMigrationSuccess({
          message: data.message,
          adminUser: data.adminUser,
        });
      }
    } catch (err) {
      setMigrationError(err?.message || 'Migration failed');
      setMigrationSuccess(null);
    } finally {
      setMigrationRunning(false);
    }
  }, [id, migrationSourceSchema, migrationSelectedAppIds, migrationPlan?.targetSchemaExists, migrationSelectedTargetUserId]);

  const handleMigrationModalOpen = useCallback(async () => {
    setShowMigrationModal(true);
    setMigrationError('');
    setMigrationSuccess(null);
    if (migrationSchemas.length === 0 && id) {
      loadMigrationSchemas();
    }
    // If we already have a source schema, refetch plan so incremental delta (remainingToMigrate) is up to date after adding tabs/objects.
    if (id && migrationSourceSchema) {
      setMigrationPlanLoading(true);
      try {
        const plan = await adminSchemaApi.fetchMigrationPlan(id, migrationSourceSchema);
        setMigrationPlan(plan);
        const targetSchema = plan?.targetSchemaExists && plan?.targetSchema ? plan.targetSchema : null;
        const apps = await adminSchemaApi.fetchStandardApps(migrationSourceSchema, targetSchema);
        setMigrationStandardApps(Array.isArray(apps) ? apps : []);
        setMigrationSelectedAppIds(Array.isArray(apps) && apps.length > 0 ? apps.map((a) => a.id) : []);
        if (plan?.message) setMigrationError(plan.message);
        if (plan?.targetSchemaExists && plan?.targetSchema) {
          setMigrationTargetUsersLoading(true);
          try {
            const users = await adminSchemaApi.fetchMigrationTargetUsers(plan.targetSchema);
            setMigrationTargetUsers(Array.isArray(users) ? users : []);
          } catch {
            setMigrationTargetUsers([]);
          } finally {
            setMigrationTargetUsersLoading(false);
          }
        } else {
          setMigrationTargetUsers([]);
        }
      } catch {
        setMigrationPlan(null);
      } finally {
        setMigrationPlanLoading(false);
      }
    } else {
      setMigrationPlan(null);
      setMigrationStandardApps([]);
      setMigrationSelectedAppIds([]);
      migrationSelectedTargetUserIdRef.current = '';
      setMigrationSelectedTargetUserId('');
      setMigrationTargetUsers([]);
    }
  }, [id, migrationSchemas.length, loadMigrationSchemas, migrationSourceSchema]);

  const handleMigrationSourceChange = useCallback(async (sourceSchema) => {
    setMigrationSourceSchema(sourceSchema);
    setMigrationPlan(null);
    setMigrationError('');
    setMigrationSelectedAppIds([]);
    setMigrationStandardApps([]);
    migrationSelectedTargetUserIdRef.current = '';
    setMigrationSelectedTargetUserId('');
    setMigrationTargetUsers([]);
    if (!id || !sourceSchema) return;
    setMigrationPlanLoading(true);
    try {
      const plan = await adminSchemaApi.fetchMigrationPlan(id, sourceSchema);
      setMigrationPlan(plan);
      const targetSchema = plan?.targetSchemaExists && plan?.targetSchema ? plan.targetSchema : null;
      const apps = await adminSchemaApi.fetchStandardApps(sourceSchema, targetSchema);
      setMigrationStandardApps(Array.isArray(apps) ? apps : []);
      setMigrationSelectedAppIds(Array.isArray(apps) && apps.length > 0 ? apps.map((a) => a.id) : []);
      if (plan?.message) setMigrationError(plan.message);
      if (plan?.targetSchemaExists && plan?.targetSchema) {
        setMigrationTargetUsersLoading(true);
        try {
          const users = await adminSchemaApi.fetchMigrationTargetUsers(plan.targetSchema);
          setMigrationTargetUsers(Array.isArray(users) ? users : []);
        } catch {
          setMigrationTargetUsers([]);
        } finally {
          setMigrationTargetUsersLoading(false);
        }
      } else {
        setMigrationTargetUsers([]);
      }
    } catch (err) {
      setMigrationError(err?.message || 'Failed to load plan');
      setMigrationPlan(null);
      setMigrationStandardApps([]);
    } finally {
      setMigrationPlanLoading(false);
    }
  }, [id]);

  const loadInvoices = useCallback(async () => {
    if (!id) return;
    setInvoicesLoading(true);
    try {
      const list = await adminSchemaApi.fetchInvoicesByCompanyId(id);
      setInvoices(Array.isArray(list) ? list : []);
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, [id]);

  const handleDeleteInvoice = useCallback(async (invoiceId) => {
    if (!id || !invoiceId || !window.confirm('Delete this invoice? Line items will be removed. This cannot be undone.')) return;
    setDeletingInvoiceId(invoiceId);
    setInvoiceError('');
    try {
      await adminSchemaApi.deleteInvoice(id, invoiceId);
      await loadInvoices();
    } catch (err) {
      setInvoiceError(err?.message || 'Failed to delete invoice');
    } finally {
      setDeletingInvoiceId(null);
    }
  }, [id, loadInvoices]);

  const handleConfirmDeleteSubscription = async () => {
    if (!subscriptionToDelete || !id) return;
    setDeletingSubscription(true);
    setDeleteSubscriptionError('');
    try {
      await adminSchemaApi.deleteSubscription(id, subscriptionToDelete.id);
      setSubscriptionToDelete(null);
      loadSubscriptions();
      loadInvoices();
    } catch (err) {
      setDeleteSubscriptionError(err?.message || 'Failed to delete subscription.');
    } finally {
      setDeletingSubscription(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setError('Company ID is missing.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const data = await adminSchemaApi.fetchCompanyById(id);
        setCompany(data);
      } catch (err) {
        setError(err?.message || 'Failed to load company.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (id && company) {
      loadSubscriptions();
      loadInvoices();
      loadCompanyUsers();
      loadLicenseTypes();
    }
  }, [id, company, loadSubscriptions, loadInvoices, loadCompanyUsers, loadLicenseTypes]);

  useEffect(() => {
    if (company?.tenantcode) {
      loadMigrationSchemas();
    }
  }, [company?.tenantcode, loadMigrationSchemas]);

  if (loading) {
    return <div className="admin-subtitle">Loading...</div>;
  }

  if (error) {
    return (
      <>
        <Alert variant="danger">{error}</Alert>
        <Link to="/admin/schemas" className="admin-primary-btn btn btn-sm">
          Back to Schemas
        </Link>
      </>
    );
  }

  if (!company) {
    return (
      <>
        <Alert variant="warning">Company not found.</Alert>
        <Link to="/admin/schemas" className="admin-primary-btn btn btn-sm">
          Back to Schemas
        </Link>
      </>
    );
  }

  const renderValue = (key, value) => {
    if (value == null || value === '') return '-';
    if (key === 'isactive' || key === 'enable_watconnect' || key === 'ispremium') {
      return value ? 'Yes' : 'No';
    }
    if (key === 'logourl' || key === 'sidebarbgurl') {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      );
    }
    return String(value);
  };

  const displayOrder = [
    'name',
    'tenantcode',
    'id',
    'userlicenses',
    'isactive',
    'systememail',
    'adminemail',
    'currency',
    'ispremium',
    'enable_watconnect',
    'street',
    'city',
    'state',
    'pincode',
    'country',
    'sourceschema',
    'logourl',
    'sidebarbgurl',
  ];

  return (
    <>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <Link to="/admin/schemas" className="btn btn-outline-secondary btn-sm">
          ← Back to Schemas
        </Link>
        <div className="d-flex align-items-center gap-2 ms-auto">
          <Form.Label className="mb-0 small text-muted">Default admin for schema</Form.Label>
          <Form.Select
            size="sm"
            style={{ width: 'auto', minWidth: '180px' }}
            value={company.default_schema_user_id ?? ''}
            onChange={handleDefaultSchemaUserChange}
            disabled={companyUsersLoading || savingDefaultSchemaUser}
          >
            <option value="">— None —</option>
            {companyUsers.map((u) => {
              const name = [u.firstname, u.lastname].filter(Boolean).join(' ') || u.username || u.email || u.id;
              const label = u.username ? `${name} (${u.username})` : name;
              return (
                <option key={u.id} value={u.id}>
                  {label}
                </option>
              );
            })}
          </Form.Select>
          {(companyUsersLoading || savingDefaultSchemaUser) && (
            <span className="small text-muted">...</span>
          )}
        </div>
      </div>
      <p className="small text-muted mb-1">
        Schema/table updates for this company use this user for createdbyid/lastmodifiedbyid when applicable.
      </p>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="admin-title mb-1">{company.name || 'Company'}</h2>
          <p className="admin-subtitle mb-0">Company details</p>
        </div>
        {company.tenantcode && (
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              className="align-self-center"
              onClick={() => setShowFieldMigrationModal(true)}
            >
              Field migration
            </Button>
            <Button className="admin-primary-btn" onClick={handleMigrationModalOpen}>
              Start Migration
            </Button>
          </div>
        )}
      </div>

      <Row className="admin-detail-layout" style={{ marginLeft: 0, marginRight: 0 }}>
        {/* Left column: main detail content (50% on lg) */}
        <Col xs={12} lg={6} className="pe-lg-2 mb-3 mb-lg-0">
          <div className="admin-detail-left-panel">
            <div className="admin-detail-widget-title">Details</div>
            <div className="admin-detail-grid">
              {displayOrder.map(key => (
                <div key={key}>
                  <strong className="text-muted small">{FIELD_LABELS[key] || key}</strong>
                  <div className="mt-1">{renderValue(key, company[key])}</div>
                </div>
              ))}
            </div>
          </div>
        </Col>

        {/* Right column: widgets / sidebar (50% on lg) */}
        <Col xs={12} lg={6} className="ps-lg-2">
          <div className="admin-detail-widgets-panel">
            {/* Widget: Subscriptions */}
            <div className="admin-detail-widget-card">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <span className="admin-detail-widget-title mb-0">Subscriptions</span>
                <Button
                  size="sm"
                  className="admin-primary-btn"
                  onClick={() => {
                    setEditingSubscription(null);
                    setShowNewSubscriptionModal(true);
                  }}
                >
                  New subscription
                </Button>
              </div>
              {subscriptionsLoading ? (
                <p className="admin-subtitle small mb-0">Loading…</p>
              ) : subscriptions.length === 0 ? (
                <p className="admin-subtitle small mb-0">No subscriptions yet. Create one to get started.</p>
              ) : (
                <Table size="sm" bordered responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>Subscription #</th>
                      <th>Cycle</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th className="text-center">Config</th>
                      <th className="text-center">Edit</th>
                      <th className="text-center">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => {
                      const isActive = (sub.status || '').toUpperCase() === 'ACTIVE';
                      return (
                        <tr key={sub.id}>
                          <td><strong>{sub.name || '—'}</strong></td>
                          <td className="text-capitalize">{sub.billing_cycle}</td>
                          <td>{formatDate(sub.start_date)}</td>
                          <td>{formatDate(sub.end_date)}</td>
                          <td>{sub.plan_name || sub.plan_code || '—'}</td>
                          <td>{sub.status || '-'}</td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              className="p-0 text-info"
                              title="Override config"
                              onClick={() => setSubscriptionConfigTarget(sub)}
                              disabled={!sub.plan_id}
                            >
                              <FaCog />
                            </Button>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              className="p-0 text-secondary"
                              title="Edit subscription"
                              onClick={() => {
                                setEditingSubscription(sub);
                                setShowNewSubscriptionModal(true);
                              }}
                            >
                              <FaEdit />
                            </Button>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              className="p-0 text-danger"
                              title={isActive ? 'Only non-active subscriptions can be deleted' : 'Delete subscription and all its licenses, invoices'}
                              disabled={isActive}
                              onClick={() => setSubscriptionToDelete(sub)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>

            <NewSubscriptionModal
              show={showNewSubscriptionModal}
              onHide={() => {
                setShowNewSubscriptionModal(false);
                setEditingSubscription(null);
              }}
              companyId={id}
              onSuccess={loadSubscriptions}
              initialSubscription={editingSubscription}
            />

            <SubscriptionConfigModal
              show={!!subscriptionConfigTarget}
              onHide={() => setSubscriptionConfigTarget(null)}
              companyId={id}
              subscription={subscriptionConfigTarget}
              onSuccess={loadSubscriptions}
            />

            <Modal show={!!subscriptionToDelete} onHide={() => !deletingSubscription && setSubscriptionToDelete(null)} centered>
              <Modal.Header closeButton>
                <Modal.Title className="admin-title">Delete subscription</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {deleteSubscriptionError && (
                  <Alert variant="danger" dismissible onClose={() => setDeleteSubscriptionError('')}>
                    {deleteSubscriptionError}
                  </Alert>
                )}
                {subscriptionToDelete && (
                  <p className="mb-0">
                    Permanently delete this subscription? This will remove the subscription and{' '}
                    <strong>all its licenses, invoices and invoice line items</strong>. This cannot be undone.
                  </p>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setSubscriptionToDelete(null)} disabled={deletingSubscription}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleConfirmDeleteSubscription} disabled={deletingSubscription}>
                  {deletingSubscription ? 'Deleting…' : 'Delete'}
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Widget: License types – one per (subscription, license type); configure entitlements here */}
            <div className="admin-detail-widget-card">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <span className="admin-detail-widget-title mb-0">License types</span>
                <Button
                  size="sm"
                  className="admin-primary-btn"
                  disabled={reconcileAdminTabsLoading || !company?.tenantcode}
                  title="For roles on an Admin license: align permission_config.tabs with the subscription and Admin license (add missing standard tabs, remove disallowed standard tabs; custom tab names kept)."
                  onClick={async () => {
                    if (!id) return;
                    setReconcileAdminTabsMessage('');
                    setReconcileAdminTabsLoading(true);
                    try {
                      const data = await adminSchemaApi.reconcileAdminTabs(id);
                      setReconcileAdminTabsAlertVariant('success');
                      setReconcileAdminTabsMessage(data?.message || 'Done.');
                    } catch (e) {
                      setReconcileAdminTabsAlertVariant('danger');
                      setReconcileAdminTabsMessage(e?.message || 'Failed.');
                    } finally {
                      setReconcileAdminTabsLoading(false);
                    }
                  }}
                >
                  {reconcileAdminTabsLoading ? 'Reconciling…' : 'Reconcile Admin tabs'}
                </Button>
              </div>
              {reconcileAdminTabsMessage ? (
                <Alert
                  variant={reconcileAdminTabsAlertVariant}
                  className="py-2 px-3 small mb-2"
                  dismissible
                  onClose={() => setReconcileAdminTabsMessage('')}
                >
                  {reconcileAdminTabsMessage}
                </Alert>
              ) : null}
              <p className="admin-subtitle small mb-2">
                One row per license type per subscription. Configure entitlements (gear) for all seats of that type.
              </p>
              {licenseTypesLoading ? (
                <p className="admin-subtitle small mb-0">Loading…</p>
              ) : licenseTypes.length === 0 ? (
                <p className="admin-subtitle small mb-0">
                  No license types yet. Add licenses to a subscription below; they will appear here.
                </p>
              ) : (
                <Table size="sm" bordered responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>Subscription</th>
                      <th>License type</th>
                      <th>Qty</th>
                      <th>Used</th>
                      <th className="text-center">Entitlements</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenseTypes.map((row) => (
                      <tr key={row.id}>
                        <td className="text-capitalize">
                          {row.subscription_name || '—'} · {row.billing_cycle} · {formatDate(row.sub_start_date)} – {formatDate(row.sub_end_date)}
                        </td>
                        <td>{row.license_type_name || row.license_type_code || '—'}</td>
                        <td>{row.quantity ?? '—'}</td>
                        <td>{row.used_count != null ? row.used_count : '—'}</td>
                        <td className="text-center">
                          <Button
                            variant="link"
                            className="p-0 text-info"
                            title="Configure entitlements for this license type"
                            onClick={() => setLicenseTypeEntitlementsTarget(row)}
                          >
                            <FaCog />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            {/* Widget: Licenses – all licenses for current subscriptions + add license */}
            <div className="admin-detail-widget-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="admin-detail-widget-title mb-0">Licenses</span>
                <Button
                  size="sm"
                  className="admin-primary-btn"
                  onClick={() => {
                    setEditingLicense(null);
                    setShowAddLicenseModal(true);
                  }}
                  disabled={subscriptions.length === 0}
                  title={subscriptions.length === 0 ? 'Create a subscription first' : 'Add license'}
                >
                  Add license
                </Button>
              </div>
              {subscriptionsLoading ? (
                <p className="admin-subtitle small mb-0">Loading…</p>
              ) : subscriptions.length === 0 ? (
                <p className="admin-subtitle small mb-0">No subscriptions. Create one to add licenses.</p>
              ) : (() => {
                const totalLicenses = subscriptions.reduce((sum, sub) => sum + (sub.licenses || []).length, 0);
                if (totalLicenses === 0) {
                  return (
                    <p className="admin-subtitle small mb-0">
                      No licenses yet for current subscriptions. Use &quot;Add license&quot; to add one.
                    </p>
                  );
                }
                const rows = subscriptions.flatMap((sub) =>
                  (sub.licenses || []).map((lic) => ({
                    sub,
                    lic,
                  }))
                );
                return (
                  <Table size="sm" bordered responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>License #</th>
                        <th>Subscription</th>
                        <th>License</th>
                        <th>Qty</th>
                        <th>Status</th>
                        <th>Start</th>
                        <th className="text-center">Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ sub, lic }) => (
                        <tr key={lic.id}>
                          <td><strong>{lic.name || '—'}</strong></td>
                          <td className="text-capitalize">
                            {sub.billing_cycle} · {formatDate(sub.start_date)} – {formatDate(sub.end_date)}
                          </td>
                          <td>{lic.license_type_name || lic.license_type_code || 'License'}</td>
                          <td>{lic.quantity}</td>
                          <td className="text-capitalize">{lic.status || '—'}</td>
                          <td>{formatDate(lic.start_date)}</td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              className="p-0 text-secondary"
                              title="Edit license"
                              onClick={() => {
                                setEditingLicense({
                                  ...lic,
                                  subscription_id: sub.id,
                                });
                                setShowAddLicenseModal(true);
                              }}
                            >
                              <FaEdit />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                );
              })()}
            </div>

            <AddLicenseModal
              show={showAddLicenseModal}
              onHide={() => {
                setShowAddLicenseModal(false);
                setEditingLicense(null);
              }}
              companyId={id}
              subscriptions={subscriptions}
              onSuccess={loadSubscriptions}
              initialLicense={editingLicense}
            />

            {licenseTypeEntitlementsTarget && (
              <LicenseTypeEntitlementsModal
                show={!!licenseTypeEntitlementsTarget}
                onHide={() => setLicenseTypeEntitlementsTarget(null)}
                companyId={id}
                licenseTypeRecord={licenseTypeEntitlementsTarget}
                subscription={subscriptions.find((s) => s.id === licenseTypeEntitlementsTarget.subscription_id) || null}
                onSuccess={() => {
                  loadLicenseTypes();
                  loadSubscriptions();
                }}
              />
            )}

            {/* Widget: Invoices – list and create against current subscriptions */}
            <div className="admin-detail-widget-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="admin-detail-widget-title mb-0">Invoices</span>
                <Button
                  size="sm"
                  className="admin-primary-btn"
                  onClick={() => setShowCreateInvoiceModal(true)}
                  disabled={subscriptions.length === 0}
                  title={subscriptions.length === 0 ? 'Create a subscription first' : 'Create invoice'}
                >
                  Create invoice
                </Button>
              </div>
              {invoicesLoading ? (
                <p className="admin-subtitle small mb-0">Loading…</p>
              ) : invoices.length === 0 ? (
                <p className="admin-subtitle small mb-0">
                  No invoices yet. Create an invoice against a subscription (e.g. for additional licenses).
                </p>
              ) : (
                <>
                  {invoiceError && (
                    <Alert variant="danger" dismissible onClose={() => setInvoiceError('')} className="mb-2">
                      {invoiceError}
                    </Alert>
                  )}
                  <Table size="sm" bordered responsive className="mb-0">
                  <thead>
                    <tr>
                        <th>Date</th>
                        <th>Pricebook</th>
                        <th>Subscription</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th style={{ width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>{formatDate(inv.invoice_date)}</td>
                        <td>{inv.pricebook_name ? `${inv.pricebook_name} (${inv.pricebook_currency ? formatCurrencyLabel(inv.pricebook_currency) : '-'})` : '-'}</td>
                        <td className="text-capitalize">
                          {inv.billing_cycle} · {formatDate(inv.sub_start_date)} – {formatDate(inv.sub_end_date)}
                        </td>
                        <td>{inv.total != null ? formatCurrency(inv.total, inv.pricebook_currency) : '-'}</td>
                        <td>{inv.status ? <span className="badge bg-secondary">{inv.status}</span> : '-'}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => setEditingInvoice(inv)}
                            title="Edit invoice"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteInvoice(inv.id)}
                            disabled={deletingInvoiceId === inv.id || (inv.status || '').toUpperCase() !== 'DRAFT'}
                            title={(inv.status || '').toUpperCase() === 'DRAFT' ? 'Delete invoice' : 'Only draft invoices can be deleted'}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </>
              )}
            </div>

            <CreateInvoiceModal
              show={showCreateInvoiceModal}
              onHide={() => setShowCreateInvoiceModal(false)}
              companyId={id}
              subscriptions={subscriptions}
              onSuccess={() => {
                loadInvoices();
                loadSubscriptions();
              }}
            />
            <EditInvoiceModal
              show={!!editingInvoice}
              onHide={() => setEditingInvoice(null)}
              companyId={id}
              invoice={editingInvoice}
              subscriptions={subscriptions}
              onSuccess={() => {
                loadInvoices();
                setEditingInvoice(null);
              }}
            />

            {/* Start Migration modal: select source schema, see what will be migrated, then create schema / run */}
            <Modal
              show={showMigrationModal}
              onHide={() => setShowMigrationModal(false)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title className="admin-title">Start migration</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="admin-subtitle small mb-3">
                  Copy objects from a source schema into this company&apos;s schema ({company?.tenantcode}). Select a source schema to see what will be migrated.
                </p>
                {migrationError && (
                  <Alert
                    id="migration-modal-error-banner"
                    variant="warning"
                    dismissible
                    onClose={() => setMigrationError('')}
                    className="mb-3"
                  >
                    {migrationError}
                  </Alert>
                )}
                {migrationSuccess && (
                  <Alert variant="success" dismissible onClose={() => setMigrationSuccess(null)} className="mb-3">
                    {migrationSuccess.message}
                    {migrationSuccess.adminUser && typeof migrationSuccess.adminUser === 'object' && migrationSuccess.adminUser.email && (
                      <div className="small mt-2">
                        You can log in with <strong>{migrationSuccess.adminUser.email}</strong> (same password as in source schema) to verify.
                      </div>
                    )}
                    {migrationSuccess.adminUser && typeof migrationSuccess.adminUser === 'string' && (
                      <div className="small mt-2">{migrationSuccess.adminUser}</div>
                    )}
                  </Alert>
                )}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold">Source schema</Form.Label>
                  <Form.Select
                    value={migrationSourceSchema}
                    onChange={(e) => handleMigrationSourceChange(e.target.value)}
                  >
                    <option value="">Select source schema…</option>
                    {migrationSchemas.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {migrationPlanLoading && (
                  <p className="small text-muted mb-2">Loading plan…</p>
                )}
                {migrationPlan?.targetSchemaExists === true && (
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Target user (for createdbyid / lastmodifiedbyid)</Form.Label>
                    <Form.Select
                      value={migrationSelectedTargetUserId}
                      onChange={(e) => {
                        const v = e.target.value;
                        migrationSelectedTargetUserIdRef.current = v;
                        setMigrationSelectedTargetUserId(v);
                      }}
                      isInvalid={migrationPlan?.targetSchemaExists && !migrationSelectedTargetUserId}
                    >
                      <option value="">Select a user…</option>
                      {migrationTargetUsersLoading ? (
                        <option disabled>Loading…</option>
                      ) : (
                        migrationTargetUsers.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.email || u.username || u.id} {u.firstname || u.lastname ? `(${[u.firstname, u.lastname].filter(Boolean).join(' ')})` : ''}
                          </option>
                        ))
                      )}
                    </Form.Select>
                    <Form.Text className="text-muted small">
                      Required for incremental migration. All migrated records will use this user for createdbyid and lastmodifiedbyid.
                    </Form.Text>
                  </Form.Group>
                )}
                {migrationPlan && migrationStandardApps.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Apps to migrate</Form.Label>
                    <MultiSelectDropdown
                      selectedValues={migrationSelectedAppIds}
                      picklistValues={migrationStandardApps.map((a) => ({ key: a.id, value: a.name || a.id }))}
                      onChange={setMigrationSelectedAppIds}
                      placeholder="Select apps to migrate (standard only)"
                    />
                    <Form.Text className="text-muted small">
                      Only standard apps from the source schema are listed. Selected apps will be copied to the target schema.
                    </Form.Text>
                  </Form.Group>
                )}
                {migrationPlan && !migrationPlanLoading && (
                  <div className="border rounded p-3 bg-light mb-3">
                    <div className="small fw-semibold mb-2">What will be migrated</div>
                    <p className="small text-muted mb-2">
                      Only objects in this company&apos;s subscription config (objectManager.allowedObjects / allowedObjectsPermission) that exist in the source schema are included.
                    </p>
                    <p className="small mb-1"><strong>Target schema:</strong> {migrationPlan.targetSchema || '—'}</p>
                    {migrationPlan.targetSchemaExists === false && (
                      <p className="small text-info mb-2">
                        Target schema does not exist yet. When you run &quot;Start migration&quot;, the schema will be created (structure copied from source), then the objects below will be copied. Later runs will only migrate remaining objects (incremental).
                      </p>
                    )}
                    {migrationPlan.targetSchemaExists === true && (
                      <p className="small text-info mb-2">
                        Schema already exists. Only <strong>remaining</strong> objects (and new tabs in allowed list) are migrated. After adding tabs or allowed objects to the subscription, run &quot;Start migration&quot; again for incremental sync.
                      </p>
                    )}
                    <p className="small mb-1">
                      <strong>{migrationPlan.targetSchemaExists ? 'Remaining to migrate:' : 'Objects to migrate:'}</strong>{' '}
                      {Array.isArray(migrationPlan.remainingToMigrate) ? migrationPlan.remainingToMigrate.length : 0} object(s)
                    </p>
                    {migrationPlan?.systemSettings?.sourceHasRow && migrationPlan?.systemSettings?.willCreateOnRun && (
                      <p className="small text-warning mb-2">
                        <strong>System Settings:</strong> target org has no system settings record. It will be created from source during this migration run.
                      </p>
                    )}
                    {migrationPlan?.systemSettings?.sourceHasRow && migrationPlan?.systemSettings?.targetHasRow === true && (
                      <p className="small text-muted mb-2">
                        <strong>System Settings:</strong> currency-related settings will be synced during migration.
                      </p>
                    )}
                    {Array.isArray(migrationPlan.remainingToMigrate) && migrationPlan.remainingToMigrate.length > 0 && (
                      <>
                        <ul className="small mb-1 ps-3">
                          {allowedObjectsForDisplay(migrationPlan.remainingToMigrate).map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                        <p className="small text-muted mb-2">
                          Activity, Task, Note, Email log, User, Role, and Password reset are always migrated and are not listed above. Product is chosen in plan/subscription allowed objects; product and product_price also migrate when Opportunity, Order, or Quote is included.
                        </p>
                      </>
                    )}
                    {Array.isArray(migrationPlan.tabsToAdd) && migrationPlan.tabsToAdd.length > 0 && (
                      <>
                        <p className="small mb-1"><strong>Tabs to add:</strong> {migrationPlan.tabsToAdd.length}</p>
                        <ul className="small mb-2 ps-3">
                          {migrationPlan.tabsToAdd.map((t) => (
                            <li key={t.id || t.name}>{t.label || t.name || t.object_name || '—'}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {Array.isArray(migrationPlan.tabsToRemove) && migrationPlan.tabsToRemove.length > 0 && (
                      <>
                        <p className="small mb-1"><strong>Tabs to remove:</strong> {migrationPlan.tabsToRemove.length}</p>
                        <ul className="small mb-2 ps-3">
                          {migrationPlan.tabsToRemove.map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    <details className="small mt-2">
                      <summary className="text-muted cursor-pointer">Per-object deliverable items (configuration)</summary>
                      <ul className="small mb-1 ps-3 mt-1">
                        {Array.isArray(migrationPlan.deliverableItems) && migrationPlan.deliverableItems.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                      {Array.isArray(migrationPlan.deliverableSchemaTables) && migrationPlan.deliverableSchemaTables.length > 0 && (
                        <>
                          <p className="small fw-semibold mb-1 mt-2">Schema tables (structure created with schema)</p>
                          <ul className="small mb-1 ps-3">
                            {migrationPlan.deliverableSchemaTables.map((t) => (
                              <li key={t}>{t}</li>
                            ))}
                          </ul>
                        </>
                      )}
                      {migrationPlan.deliverableRecordDataNote && (
                        <p className="small text-muted mb-0 mt-1">{migrationPlan.deliverableRecordDataNote}</p>
                      )}
                    </details>
                  </div>
                )}
                {migrationPlan && (
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="admin-primary-btn"
                      onClick={handleStartMigration}
                      disabled={
                        migrationRunning
                        || (migrationPlan?.targetSchemaExists === true && !migrationSelectedTargetUserId)
                        || (
                          (Array.isArray(migrationPlan?.remainingToMigrate) ? migrationPlan.remainingToMigrate.length : 0) === 0
                          && (Array.isArray(migrationPlan?.tabsToAdd) ? migrationPlan.tabsToAdd.length : 0) === 0
                          && (Array.isArray(migrationPlan?.tabsToRemove) ? migrationPlan.tabsToRemove.length : 0) === 0
                          && !migrationPlan?.systemSettings?.willCreateOnRun
                          && (Array.isArray(migrationSelectedAppIds) ? migrationSelectedAppIds.length : 0) === 0
                        )
                      }
                      title={
                        migrationPlan?.targetSchemaExists === true && !migrationSelectedTargetUserId
                          ? 'Select a target user for incremental migration'
                          : (Array.isArray(migrationPlan?.remainingToMigrate) && migrationPlan.remainingToMigrate.length > 0)
                            || (Array.isArray(migrationPlan?.tabsToAdd) && migrationPlan.tabsToAdd.length > 0)
                            || (Array.isArray(migrationPlan?.tabsToRemove) && migrationPlan.tabsToRemove.length > 0)
                            || migrationPlan?.systemSettings?.willCreateOnRun
                            || (Array.isArray(migrationSelectedAppIds) && migrationSelectedAppIds.length > 0)
                              ? 'Run migration for remaining objects, tab changes, and/or selected apps'
                              : 'Nothing remaining to migrate (no objects, tab add/remove, or apps selected)'
                      }
                    >
                      {migrationRunning ? 'Running…' : 'Start migration'}
                    </Button>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowMigrationModal(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </Col>
      </Row>

      <AdminFieldMigrationModal
        show={showFieldMigrationModal}
        onHide={() => setShowFieldMigrationModal(false)}
        company={company}
      />
    </>
  );
};

export default AdminCompanyDetail;
